<script setup lang="ts">
/**
 * The layout of the portal (ADR-066: one layout component): sidebar with the navigation the
 * session allows and its counters (src/composables/navigation.ts), header with login or user
 * menu, the page, the footer. The login dialog opens
 * over the catalogue on `/login` (07 §5, AP-F4); closing it leads to the catalogue. The inquiry
 * dialog is here once for every page (AP-F6.5). From the login "Für die Beta bewerben" leads to
 * the application page `/beta`, after a login refused with `instance_not_enabled` with the entry
 * `?lead=instance` (ADR-143 addendum 3). With the guest view off (ADR-133 point 3) the login
 * stands on a page of its own and cannot be closed (TL 2026-10-20); the application page is open
 * then too, and the way back leads to the login.
 */
import { computed, ref, useTemplateRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppFooter from "./components/AppFooter.vue";
import AppHeader from "./components/AppHeader.vue";
import AppSidebar from "./components/AppSidebar.vue";
import InquiryDialog from "./components/InquiryDialog.vue";
import LoginDialog from "./components/LoginDialog.vue";
import { useInquiryDialog } from "./composables/inquiry.ts";
import { useNavCounts, useProductFamilyItem } from "./composables/navigation.ts";
import { SESSION_ENDED_FLAG } from "./router/index.ts";
import { navigationFor } from "./router/navigation.ts";
import { useSession } from "./session/session.ts";
import { usePortalSettings } from "./session/settings.ts";

const route = useRoute();
const router = useRouter();
const { session, isGuest, isPending } = useSession();
const settings = usePortalSettings();
const guestCatalog = computed(() => settings.value.guestCatalog);

const groups = computed(() => navigationFor(session.value, settings.value));
const counts = useNavCounts(session);
const familyItem = useProductFamilyItem();
const drawerOpen = ref(false);
watch(
  () => route.fullPath,
  () => (drawerOpen.value = false),
);
/** The header, whose drawer button gets the focus back when the drawer closes (PF9). */
const header = useTemplateRef<InstanceType<typeof AppHeader>>("header");

const queryText = (value: unknown) => (typeof value === "string" ? value : undefined);

function openLogin() {
  void router.push({ name: "login", query: { next: route.fullPath } });
}

function closeLogin() {
  void router.replace({ name: "catalog" });
}

const inquiry = useInquiryDialog();

/**
 * From the login: apply for the beta instead (ADR-093 point 8; in the alpha release in place of
 * asking for access, TL 2026-10-27), on the application page (ADR-143 addendum 3). After a login
 * refused for an instance not enabled with its entry (`instance`), whose note says why; without
 * an instance without one. Pushed, so the way back is the login.
 */
function apply(lead?: "instance") {
  void router.push({ name: "application", query: lead ? { lead } : {} });
}
</script>

<template>
  <a
    href="#main"
    class="focus:rounded-control focus:text-ui focus:text-text-head focus:shadow-popover sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-(--z-toast) focus:bg-(--color-surface) focus:px-4 focus:py-2 focus:font-semibold"
  >
    Zum Inhalt springen
  </a>
  <AppSidebar
    :open="drawerOpen"
    :groups="groups"
    :is-guest="isGuest"
    :guest-catalog="guestCatalog"
    :session="session"
    :counts="counts"
    :family-item="familyItem"
    :opener="header?.menuButton ?? null"
    @close="drawerOpen = false"
    @login="openLogin"
  />
  <!-- inert behind the open drawer, so the focus stays in it (GU4); without the attribute otherwise,
       since an attribute inert="false" would still make it inert where `inert` is no property -->
  <div class="bg-canvas flex min-h-dvh flex-col md:pl-(--size-sidebar)" :inert="drawerOpen || undefined" data-page>
    <AppHeader ref="header" :session="session" :pending="isPending" @open-menu="drawerOpen = true" @login="openLogin" />
    <main id="main" class="px-safety flex-1 py-8 md:px-8" tabindex="-1">
      <RouterView />
    </main>
    <AppFooter />
  </div>
  <LoginDialog
    v-if="route.meta.loginDialog && !inquiry.request.value"
    :next="queryText(route.query.next)"
    :error="queryText(route.query.error)"
    :session-ended="route.query[SESSION_ENDED_FLAG] === '1'"
    :closable="guestCatalog"
    @close="closeLogin"
    @apply="apply"
  />
  <InquiryDialog />
</template>
